import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { ISpRewardBrand } from '@brushfire/types'
import { isDescriptionClampedOverflowing } from '../lib/descriptionClamp'
import {
  selectBrandCardDescriptionClampedSx,
  selectBrandCardDescriptionSx,
  selectBrandCardReadMoreSx,
  selectBrandCardSelectButtonSx,
  selectBrandCardSx,
  selectBrandCardTitleSx,
  selectBrandLogoPlaceholderSx,
  selectBrandLogoSx,
} from '../styles/selectBrandStyles'
import { BrandLogoImage } from './BrandLogoImage'

interface Props {
  brand: ISpRewardBrand
  onSelect: (brand: ISpRewardBrand) => void
}

export function BrandCard({ brand, onSelect }: Readonly<Props>) {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const descriptionBlockRef = useRef<HTMLDivElement>(null)

  const description = brand.description.trim()

  const syncReadMoreVisibility = useCallback(() => {
    if (!description) {
      setShowReadMore(false)
      return
    }
    if (descriptionExpanded) {
      setShowReadMore(true)
      return
    }
    const el = descriptionRef.current
    setShowReadMore(el ? isDescriptionClampedOverflowing(el) : false)
  }, [description, descriptionExpanded])

  useLayoutEffect(() => {
    syncReadMoreVisibility()
  }, [syncReadMoreVisibility])

  useLayoutEffect(() => {
    const node = descriptionBlockRef.current
    if (!node || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(() => {
      syncReadMoreVisibility()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [syncReadMoreVisibility])

  return (
    <Box
      sx={selectBrandCardSx}
      data-testid={`rewards-brand-card-${brand.productId}`}
    >
      <Box sx={{ display: 'flex', gap: '7px', alignItems: 'flex-start', mb: '10px' }}>
        <BrandLogoImage
          src={brand.logoUrl}
          imageSx={selectBrandLogoSx}
          placeholderSx={selectBrandLogoPlaceholderSx}
          testId={`rewards-brand-logo-${brand.productId}`}
        />
        <Typography component="h3" sx={selectBrandCardTitleSx}>
          {brand.name}
        </Typography>
      </Box>

      {description ? (
        <Box
          ref={descriptionBlockRef}
          sx={{
            position: 'relative',
            flex: descriptionExpanded ? '0 0 auto' : 1,
            minHeight: 0,
            mb: 1,
          }}
        >
          <Typography
            ref={descriptionRef}
            component="p"
            sx={
              descriptionExpanded
                ? selectBrandCardDescriptionSx
                : selectBrandCardDescriptionClampedSx
            }
          >
            {description}
          </Typography>
          {showReadMore ? (
            <Box
              component="button"
              type="button"
              onClick={() => setDescriptionExpanded((prev) => !prev)}
              sx={selectBrandCardReadMoreSx}
              data-testid={`rewards-brand-read-more-${brand.productId}`}
              aria-expanded={descriptionExpanded}
            >
              {descriptionExpanded ? 'Read less' : 'Read more'}
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ mt: 'auto', pt: description ? 0 : 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => onSelect(brand)}
          data-testid={`rewards-brand-select-${brand.productId}`}
          sx={selectBrandCardSelectButtonSx}
        >
          Select
        </Button>
      </Box>
    </Box>
  )
}
